import { type Order, type OrderBy } from '@/hooks/useSortPagination'

export enum ProductSearchParams {
  productName = 'Product Name',
  productId = 'Product ID',
  skuId = 'SKU ID',
  ean = 'SKU EAN',
  skuName = 'SKU Name',
  productRefId = 'Product Reference Code',
  skuRefId = 'SKU Reference Code',
  none = 'None',
}

export const FilterNull = 'Null' as const

type FilterNull = typeof FilterNull
type LoadingStatus = 'Success' | 'Failed'

export enum FilterStatus {
  publishedOrPending = 'Published/Pending',
  inactive = 'Inactive',
}

export enum FilterKit {
  activeKit = 'Active kit',
  inactiveKit = 'No active kit',
}

export enum FilterStoreVisibility {
  visible = 'Visible on store',
  hidden = 'Not visible on store',
}

export enum FilterCreationDate {
  today = 'Today',
  'last-7-days' = 'Last 7 days',
  'last-30-days' = 'Last 30 days',
  custom = 'Custom',
}

interface ProductListFilterProperties {
  'Filter Brand': boolean
  'Filter Category': boolean
  'Filter Creation Date': FilterCreationDate | FilterNull
  'Filter Kit': FilterKit | FilterNull
  'Filter Sales Channel': boolean
  'Filter Seller': boolean
  'Filter Status': FilterStatus | FilterNull
  'Filter Store Visibility': FilterStoreVisibility | FilterNull
  'Sort by': SortByEvent
  'Is Pinned': boolean
}

export type ClickOnSearchFieldEvent = {
  Action: 'Click' | 'Shortcut' | 'Automatic focus'
}

export type SortByEvent = Lowercase<`${OrderBy}:${Order}`>

export interface ProductsLoadEvent extends ProductListFilterProperties {
  Action: 'Access product listing' | 'Search'
  'Loading Time': number
  'Loading Status': LoadingStatus
  'Search Parameter'?: ProductSearchParams
  'Search Term'?: string
  'Search Results': number
  'Sort by': SortByEvent
  Pagination: boolean
}

type ClickActions =
  | 'Edit product'
  | 'Edit SKU'
  | 'Quick action: activate/inactivate SKU'
  | 'Quick action: copy SKU ID'
  | 'Quick action: edit inventory'
  | 'Quick action: edit price'
  | "Quick action: view offer's table"
  | 'Quick action: view in store'
  | 'More options: edit product'
  | 'More options: View SKU list'
  | 'More options: edit similar category'
  | 'More options: edit prices'
  | 'More options: edit inventory'
  | 'More options: view in store'
  | 'More options: view indexed info'
  | "More options: view offer's table"
  | 'More options: duplicate product'
  | 'Sort listing'
  | 'Expand/Collapse SKUs: Click'
  | 'Expand/Collapse SKUs: Shortcut'

interface ClickOnTheListEventBase {
  Action: ClickActions
  'Search Parameter'?: ProductSearchParams
}

interface ClickOnTheListEventOrder {
  Action: 'Sort listing'
  'Sort by': SortByEvent
}

export type ClickOnTheListEvent =
  | ClickOnTheListEventBase
  | ClickOnTheListEventOrder

export type SaveStatus = 'Dropped' | 'Changes saved' | 'Error on saving'

export interface SaveProductEvent<
  SwitchInputType = 'Activated' | 'Deactivated' | 'None',
> {
  Action: 'Create product' | 'Update product'
  Status: SaveStatus
  'Time spent': number
  'SKUs created': number
  'SKUs updated': number
  'Edit releaseDate': boolean
  'Edit similarCategories': boolean
  'Edit globalCategories': boolean
  'Edit complementaryDescription': boolean
  'Edit productSpecs': number
  'Edit defaultFields': number
  'Edit productName': boolean
  'Edit keywords': boolean
  'Edit brand': boolean
  'Edit category': boolean
  'Edit productRefId': boolean
  'Edit description': boolean
  'Edit linkId': boolean
  'Edit pageTitle': boolean
  'Edit metaTagDescription': boolean
  'Edit salesChannel': boolean
  'Edit supplier': boolean
  'Edit taxCode': boolean
  'Edit searchPriorityScore': boolean
  'Edit productVisibility': SwitchInputType
  'Edit showWithoutStock': SwitchInputType
  'Edit productStatus': SwitchInputType
  'All Fields Edited': number
}

export interface AccessProductFormEvent {
  Action: 'Access existing item' | 'Create new item'
  'Landed at': 'Product Form' | 'SKU Form'
  'Page Loading Time': number
  'Page Loading Status': LoadingStatus
  'Product ID': string
  'SKU ID': string
  'Quantity of SKUs'?: number
}

type FieldCopy = 'None' | 'Images'

export interface SaveSKUEvent {
  Action: 'Create SKU' | 'Update SKU'
  Status: SaveStatus
  'Time spent': number
  'Product ID': string
  'SKU ID': string
  'SKUs Created together': number
  'SKUs Updated together': number
  'All Fields Edited': number
  'Edit SKU Specs': number
  'Edit SKU Status': 'Activated' | 'Deactivated' | 'None'
  'Edited images': boolean
  'Added images': number
  'Removed images': number
  'Reorder images': boolean
  'Edited images meta data': boolean
  'Fields copied to another SKU': FieldCopy
  'Fields copied from another SKU': FieldCopy
  'Avg. Time Spent per SKU': number
}

type SpecificationProps = {
  Status: SaveStatus
  /**
   * Time spent since the Drawer open and the action is finish
   */
  'Time spent': number
  /**
   * Number of values created during the event.
   */
  'Values created': number
  /**
   * Number of values edited during the event. Either by reordering, activating/deactivating or changing name.
   */
  'Values edited': number
  'Values reordered': boolean
  'Edit required flag': boolean
  'Edit default value': boolean
  'Edit show specification flag': boolean
  'Edit use filter flag': boolean
  'Edit link in top menu flag': boolean
  'Edit link in side menu flag': boolean
}

export type SaveProductAttribute = SpecificationProps & {
  Action: 'Create product attribute' | 'Update product attribute'
  'Attribute ID': string
  'Attribute name': string
  'Attribute type': 'Text' | 'Multi-select' | 'Single-select'
  'Edit attribute status': 'Activated' | 'Deactivated' | 'None'
  'Edit attribute name': boolean
}

export type SaveSKUVariant = SpecificationProps & {
  Action: 'Create SKU variant' | 'Update SKU variant'
  'Variant ID': string
  'Variant name': string
  'Variant type': 'Single-select'
  'Edit variant status': 'Activated' | 'Deactivated' | 'None'
  'Edit variant name': boolean
}
